/**
 * PayrollStatusBadges
 *
 * Additive badges for the timesheet approval list and detail drawer.
 * Shows payroll-related status indicators alongside existing badges.
 *
 * Badges:
 *   - Missed Clock-Out (red)
 *   - Corrected by Admin (amber)
 *   - Overtime (orange)
 *   - Weekend Penalty (indigo)
 *   - Public Holiday (purple)
 *
 * DOES NOT replace existing SubmissionSourceBadge or ApprovalBadge.
 * Pure UI — reads existing timesheet/shift fields only.
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock, TrendingUp, Calendar } from 'lucide-react';
import { isLargeVariance, LARGE_VARIANCE_LABEL } from '@/utils/varianceDisplay';

export function MissedClockOutBadge({ timesheet }) {
  if (!timesheet) return null;

  const trigger = timesheet.auto_submitted_trigger || timesheet.generated_trigger;
  const isMissedCorrection = trigger === 'missed_clockout_correction';
  const isAdminClosed = trigger === 'admin_close_missed_clockout';
  const hasMissedFlag = timesheet.missed_clockout_corrected || timesheet.admin_closed_missed_clockout;

  if (isMissedCorrection || isAdminClosed || hasMissedFlag) {
    return (
      <Badge
        className="bg-amber-100 text-amber-800 border-amber-300 text-[9px]"
        title="This timesheet was generated after an admin corrected a missed clock-out."
        data-testid="badge-corrected-clockout"
      >
        <Clock className="w-2.5 h-2.5 mr-0.5" />
        Corrected
      </Badge>
    );
  }

  return null;
}

export function OvertimeBadge({ timesheet }) {
  if (!timesheet) return null;
  const otMinutes = timesheet.overtime_minutes;
  if (!otMinutes || otMinutes <= 0) return null;

  // Aug 2026 attendance fix — the chip says "OT" only; the exact duration lives
  // in the tooltip. It must never render the word "Review" (that belongs to the
  // REQUIRES_REVIEW flag) and must stay silent when the clock data behind the
  // number is known to be unusable, so a row shows one honest flag instead of
  // "Clock error" next to a fabricated "OT 2h 2m".
  if (timesheet.clock_data_invalid) return null;

  const suspect = isLargeVariance(otMinutes);
  return (
    <Badge
      className={suspect
        ? 'bg-red-100 text-red-700 border-red-300 text-[9px]'
        : 'bg-orange-100 text-orange-700 border-orange-300 text-[9px]'}
      title={suspect
        ? `${LARGE_VARIANCE_LABEL} (recorded overtime: ${otMinutes} minutes)`
        : `Overtime: ${otMinutes} minutes`}
      data-testid="badge-overtime"
    >
      {suspect
        ? <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
        : <TrendingUp className="w-2.5 h-2.5 mr-0.5" />}
      OT
    </Badge>
  );
}

export function WeekendPenaltyBadge({ timesheet }) {
  if (!timesheet) return null;
  const date = timesheet.date;
  if (!date) return null;

  try {
    const d = new Date(date + 'T12:00:00Z');
    const day = d.getUTCDay(); // 0=Sun, 6=Sat
    if (day === 6) {
      return (
        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-[9px]" data-testid="badge-saturday">
          <Calendar className="w-2.5 h-2.5 mr-0.5" />Sat
        </Badge>
      );
    }
    if (day === 0) {
      return (
        <Badge className="bg-violet-100 text-violet-700 border-violet-300 text-[9px]" data-testid="badge-sunday">
          <Calendar className="w-2.5 h-2.5 mr-0.5" />Sun
        </Badge>
      );
    }
  } catch { /* ignore */ }

  return null;
}

/**
 * Combined component — renders all applicable payroll badges for a timesheet.
 */
export function PayrollBadges({ timesheet }) {
  if (!timesheet) return null;

  return (
    <>
      <MissedClockOutBadge timesheet={timesheet} />
      <OvertimeBadge timesheet={timesheet} />
      <WeekendPenaltyBadge timesheet={timesheet} />
    </>
  );
}
